// ComplianceCockpit (TASK-583) — a client-scoped summary over the three compliance
// domains, the landing surface the review_compliance tool auto-summons:
//   • Review     — the document-based FinSA/FINMA/AML review (self-fetch)
//   • Suitability — proposal-scoped; informational here (no per-client verdict source)
//   • AML/PEP     — sanctions/PEP/adverse-media screening, ON-DEMAND (screenClient is
//                   an audited, side-effecting POST — never auto-run on mount)
// Every row is honestly independent: a missing/failed domain reads gray, never green.
// Footer summons the deep ComplianceReview tile onto the canvas.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, ShieldQuestion, Loader2 } from 'lucide-react';
import {
  complianceApiService,
  type ComplianceReviewLatest,
} from '../../../services/api/complianceApiService';
import {
  screenClient,
  type ClientScreeningResponse,
  type ScreeningCategoryStatus,
  getMonitorStatus,
  type MonitorStatus,
} from '../../../services/screeningService';
import { useCanvasActions } from '../../../stores/canvasStore';
import { WidgetContainer } from '../WidgetContainer';
import { deriveReviewBadge } from './ComplianceReview';
import { RadarTickHealth } from './RadarTickHealth';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  neutral: 'bg-accent text-muted-foreground',
};

function Row({ label, tone, value }: { label: string; tone: Tone; value: string }) {
  const Icon = tone === 'ok' ? ShieldCheck : tone === 'neutral' ? ShieldQuestion : ShieldAlert;
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="text-[12px] font-medium text-foreground">{label}</span>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}>
        <Icon className="h-3 w-3" />
        {value}
      </span>
    </div>
  );
}

// not_screened is a source gap, NOT clear — gray, never green (aml posture).
function amlFold(s: ClientScreeningResponse | undefined): { tone: Tone; value: string } {
  const party = s?.party;
  if (!party) return { tone: 'neutral', value: 'not screened' };
  const cats: ScreeningCategoryStatus[] = [
    party.categories.sanctions.status,
    party.categories.pep.status,
    party.adverse_media.status,
  ];
  if (cats.includes('potential_match')) return { tone: 'bad', value: 'potential match' };
  if (cats.includes('not_screened')) return { tone: 'neutral', value: 'partial' };
  return { tone: 'ok', value: 'clear' };
}

export function ComplianceCockpit({ clientId }: { clientId: string }) {
  const { addSpecs } = useCanvasActions();

  const { data: reviewData, isLoading: reviewLoading } = useQuery<ComplianceReviewLatest>({
    queryKey: ['compliance-review', clientId],
    queryFn: () => complianceApiService.getLatestComplianceReview(clientId),
    enabled: !!clientId,
  });
  const reviewBadge = deriveReviewBadge(reviewData?.review ?? null);

  // TASK-932: ongoing screening is a book-wide fact, not a per-client one, and until now it
  // was shown nowhere — `SCREENING_MONITORING_ENABLED=false` on the live stack and no surface
  // said so. Two facts, kept separate on the row: whether it is ON, and when it last actually
  // RAN. "on" with no run in a month is the state this row exists to expose.
  const { data: monitor } = useQuery<MonitorStatus>({
    queryKey: ['screening-monitor'],
    queryFn: getMonitorStatus,
    staleTime: 60_000,
  });
  const monitorBadge: { tone: Tone; value: string } = !monitor
    ? { tone: 'neutral', value: '…' }
    : !monitor.enabled
      ? { tone: 'neutral', value: 'off' }
      : monitor.last_run
        ? { tone: 'ok', value: `on · last run ${new Date(monitor.last_run.finished_at).toLocaleDateString()}` }
        : { tone: 'bad', value: monitor.scheduled ? 'on · never run' : 'on · not scheduled' };

  // AML is on-demand: screenClient is an audited POST. Default = not-screened gray.
  const [aml, setAml] = useState<ClientScreeningResponse>();
  const [amlLoading, setAmlLoading] = useState(false);
  const [amlError, setAmlError] = useState(false);
  const runScreen = async () => {
    setAmlLoading(true);
    setAmlError(false);
    try {
      setAml(await screenClient(clientId));
    } catch {
      setAmlError(true);
    } finally {
      setAmlLoading(false);
    }
  };
  const amlBadge = amlError
    ? { tone: 'neutral' as Tone, value: 'screen failed' }
    : aml
      ? amlFold(aml)
      : { tone: 'neutral' as Tone, value: 'not screened' };

  return (
    <WidgetContainer title="Compliance" source="Compliance">
      <div className="divide-y divide-border">
        <Row
          label="Document review"
          tone={reviewLoading ? 'neutral' : reviewBadge.tone}
          value={reviewLoading ? '…' : reviewBadge.label}
        />
        {/* ponytail: suitability is proposal-scoped; no per-client verdict source yet.
            Informational until a "latest suitability per client" read exists. */}
        <Row label="Suitability" tone="neutral" value="per proposal" />
        <Row label="AML / PEP / sanctions" tone={amlBadge.tone} value={amlLoading ? 'screening…' : amlBadge.value} />
        <Row label="Ongoing screening" tone={monitorBadge.tone} value={monitorBadge.value} />
      </div>

      {/* TASK-921: the rows above read gray when a domain is missing, but nothing said
          whether the Change Radar that feeds the compliance conditions is running at all. */}
      <div className="mt-3 border-t border-border pt-2">
        <RadarTickHealth />
      </div>

      <div className="mt-2 flex flex-wrap gap-2 border-t border-border pt-3">
        <button
          type="button"
          onClick={() => addSpecs([{ component: 'ComplianceReview', props: { clientId } }])}
          className="rounded-md border border-border px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-accent"
        >
          Open review
        </button>
        <button
          type="button"
          onClick={runScreen}
          disabled={amlLoading}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
        >
          {amlLoading && <Loader2 className="h-3 w-3 animate-spin" />}
          Screen sanctions / PEP
        </button>
      </div>
    </WidgetContainer>
  );
}
